import { useEffect, useState } from "react";
import type { PreviewOp } from "@/mutations/tasks";

interface Props {
  /** Modal heading — usually the AI action's name. */
  title: string;
  operations: PreviewOp[];
  busy?: boolean;
  onApply: (ops: PreviewOp[]) => void;
  onCancel: () => void;
}

/** Static, human-readable description for the non-editable operations. */
function describe(op: PreviewOp): string {
  switch (op.op) {
    case "delete":
      return `Удалить «${op.title}»`;
    case "complete":
      return `Завершить «${op.title}»`;
    case "uncomplete":
      return `Вернуть в активные «${op.title}»`;
    case "add_to_my_day":
      return `В «Мой день»: «${op.title}»`;
    case "remove_from_my_day":
      return `Убрать из «Мой день»: «${op.title}»`;
    case "move":
      return `Переместить «${op.title}» → «${op.bucket_name}»`;
    case "reorder":
      return `Новый порядок: ${op.items.map((i) => i.title).join(", ")}`;
    default:
      return "";
  }
}

/**
 * One editor for every AI action's output. The LLM always answers with the same
 * `operations[]` envelope, so a single component renders the proposed plan — each
 * row drawn by its `op` — and lets the user tweak it before it's applied. Text ops
 * (create_subtask/rename/set_notes) expose an editable field; every op can be
 * removed. "Apply" sends back only the surviving operations.
 */
export default function OpsPreview({ title, operations, busy = false, onApply, onCancel }: Props) {
  const [ops, setOps] = useState<PreviewOp[]>(operations);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const removeAt = (i: number) => setOps((prev) => prev.filter((_, j) => j !== i));
  const editField = (i: number, field: string, value: string) =>
    setOps((prev) => prev.map((op, j) => (j === i ? ({ ...op, [field]: value } as PreviewOp) : op)));

  function label(op: PreviewOp): string {
    if (op.op === "rename") return `«${op.title}» →`;
    if (op.op === "set_notes") return `Заметки «${op.title}»:`;
    return describe(op);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      data-testid="ops-preview-backdrop"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col"
        data-testid="ops-preview"
      >
        <div className="px-5 pt-5 pb-3">
          <h3 className="text-base font-medium text-gray-900 dark:text-white">{title}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Проверь, что предложил ИИ. Поправь заголовки, убери лишнее — и применяй.
          </p>
        </div>

        <div className="px-5 flex-1 overflow-y-auto">
          {ops.length === 0 ? (
            <p className="text-sm text-gray-400 py-3">Ничего не выбрано.</p>
          ) : (
            <ul className="space-y-1.5 py-1">
              {ops.map((op, i) => (
                <li key={i} className="flex items-center gap-2">
                  {op.op === "create_subtask" ? (
                    <input
                      type="text"
                      aria-label="Заголовок шага"
                      value={op.title}
                      onChange={(e) => editField(i, "title", e.target.value)}
                      className="flex-1 text-sm bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-blue-500 focus:outline-none py-1"
                    />
                  ) : op.op === "rename" ? (
                    <>
                      <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">{label(op)}</span>
                      <input
                        type="text"
                        aria-label="Новый заголовок"
                        value={op.new_title}
                        onChange={(e) => editField(i, "new_title", e.target.value)}
                        className="flex-1 text-sm bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-blue-500 focus:outline-none py-1"
                      />
                    </>
                  ) : op.op === "set_notes" ? (
                    <>
                      <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">{label(op)}</span>
                      <input
                        type="text"
                        aria-label="Заметки"
                        value={op.notes}
                        onChange={(e) => editField(i, "notes", e.target.value)}
                        className="flex-1 text-sm bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-blue-500 focus:outline-none py-1"
                      />
                    </>
                  ) : (
                    <span className="flex-1 text-sm text-gray-700 dark:text-gray-200 py-1">{describe(op)}</span>
                  )}
                  <button
                    type="button"
                    aria-label={`Убрать ${op.op === "reorder" ? "перестановку" : op.title}`}
                    onClick={() => removeAt(i)}
                    className="text-gray-400 hover:text-red-500 cursor-pointer px-1 flex-shrink-0"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-3 py-1.5"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => onApply(ops)}
            disabled={busy || ops.length === 0}
            className="text-sm bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-default text-white px-3 py-1.5 rounded-md font-medium"
          >
            {busy ? "Применяю…" : `Применить (${ops.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
